/**
 * Spec 041 — useThreads hook.
 *
 * Polls /api/equanaut/threads every POLL_MS and exposes
 * thread list, unread count, and CRUD operations (FR-003, FR-006, FR-012).
 *
 * Auth: requests use credentials: 'include' (FR-007).
 * Freshness: 30-second poll interval (resolved in Phase 2 clarify).
 * Org-scope: passes organizationId as query param (FR-015).
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const POLL_MS = 30_000;
const BASE = '/api/equanaut';

export type ThreadStatus = 'active' | 'archived' | 'completed';
export type Surface = 'web' | 'extension' | 'gateway';

export interface EquanautThread {
  id: string;
  organizationId: string;
  userId: string;
  title: string;
  status: ThreadStatus;
  ownerDisplay: string | null;
  orchestratorUrl: string | null;
  source: Surface;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
}

interface ThreadListResponse {
  threads: EquanautThread[];
  unreadCount: number;
  polledAt: string;
}

interface ApiErrorResponse {
  error?: string;
}

function getErrorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

// ---------------------------------------------------------------------------
// Shared fetch helper
// ---------------------------------------------------------------------------

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  if (!res.ok) {
    const body = await res.json().catch((): ApiErrorResponse => ({}));
    throw new Error(body.error ?? `Request failed (${res.status})`);
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useThreads(organizationId?: string, enabled = false) {
  const [threads, setThreads] = useState<EquanautThread[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track when we last polled so server can compute unreadCount delta (FR-012)
  const lastPolledRef = useRef<string>(new Date(0).toISOString());
  const currentOrgRef = useRef<string | undefined>(organizationId);
  const requestSeqRef = useRef(0);

  const load = useCallback(async () => {
    if (!enabled || !organizationId) return;
    const requestOrg = organizationId;
    const requestSeq = requestSeqRef.current + 1;
    requestSeqRef.current = requestSeq;
    setIsLoading(true);
    setError(null);
    try {
      const org = encodeURIComponent(requestOrg);
      const since = encodeURIComponent(lastPolledRef.current);
      const data = await apiFetch<ThreadListResponse>(
        `/threads?organizationId=${org}&since=${since}`
      );
      if (currentOrgRef.current !== requestOrg || requestSeqRef.current !== requestSeq) return;
      lastPolledRef.current = data.polledAt;
      setThreads(data.threads);
      setUnreadCount(data.unreadCount);
    } catch (err: unknown) {
      if (currentOrgRef.current === requestOrg && requestSeqRef.current === requestSeq) {
        setError(getErrorMessage(err, 'Failed to load threads'));
      }
    } finally {
      if (currentOrgRef.current === requestOrg && requestSeqRef.current === requestSeq) {
        setIsLoading(false);
      }
    }
  }, [enabled, organizationId]);

  // Initial load + 30-second poll (FR-006)
  useEffect(() => {
    if (!enabled) return;
    void load();
    const timer = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(timer);
  }, [enabled, load]);

  // On org switch: reset state immediately (FR-015, edge-case: org switch mid-poll)
  useEffect(() => {
    currentOrgRef.current = organizationId;
    requestSeqRef.current += 1;
    setThreads([]);
    setUnreadCount(0);
    setIsLoading(false);
    setError(null);
    lastPolledRef.current = new Date(0).toISOString();
  }, [organizationId]);

  // ─── CRUD operations ───────────────────────────────────────────────────────

  const createThread = useCallback(async (params: {
    title: string;
    orchestratorUrl?: string;
    source?: Surface;
  }): Promise<EquanautThread> => {
    if (!organizationId) throw new Error('organizationId required');
    const requestOrg = organizationId;
    const org = encodeURIComponent(requestOrg);
    const thread = await apiFetch<EquanautThread>(
      `/threads?organizationId=${org}`,
      {
        method: 'POST',
        body: JSON.stringify(params),
      }
    );
    if (currentOrgRef.current === requestOrg) {
      setThreads(prev => [thread, ...prev]);
    }
    return thread;
  }, [organizationId]);

  const updateThread = useCallback(async (
    id: string,
    params: { title?: string; status?: ThreadStatus }
  ): Promise<EquanautThread> => {
    if (!organizationId) throw new Error('organizationId required');
    const requestOrg = organizationId;
    const org = encodeURIComponent(requestOrg);
    const threadId = encodeURIComponent(id);
    const thread = await apiFetch<EquanautThread>(
      `/threads/${threadId}?organizationId=${org}`,
      {
        method: 'PATCH',
        body: JSON.stringify(params),
      }
    );
    if (currentOrgRef.current === requestOrg) {
      setThreads(prev => prev.map(t => (t.id === id ? thread : t)));
    }
    return thread;
  }, [organizationId]);

  const archiveThread = useCallback(async (
    id: string,
    surface: Surface = 'web'
  ): Promise<void> => {
    if (!organizationId) throw new Error('organizationId required');
    const requestOrg = organizationId;
    const org = encodeURIComponent(requestOrg);
    const threadId = encodeURIComponent(id);
    const source = encodeURIComponent(surface);
    await apiFetch(
      `/threads/${threadId}?organizationId=${org}&surface=${source}`,
      { method: 'DELETE' }
    );
    if (currentOrgRef.current === requestOrg) {
      setThreads(prev => prev.filter(t => t.id !== id));
    }
  }, [organizationId]);

  return {
    threads,
    unreadCount,
    isLoading,
    error,
    refresh: load,
    createThread,
    updateThread,
    archiveThread,
  };
}
